import { useTheme } from '@shopify/restyle';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react-native';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { addDays, addMonths, isSameDay, startOfDay, startOfMonth, subMonths, localeAwareFormat } from 'app/lib/date';
import type { Theme } from 'app/shopify';
import type { Loan } from 'app/types/loan';
import { getLoanETA, LoanETA } from 'app/types/loan';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import Animated, { LinearTransition } from 'react-native-reanimated';
import { getCalendarGridDays, loanOccursOnDate } from './loan-calendar-utils';

const MAX_VISIBLE_LOAN_MARKERS = 3;

interface LoanCalendarProps {
  currentMonth: Date;
  highlightedLoanId?: Loan['id'] | null;
  isDesktop?: boolean;
  loans: Loan[];
  onDateHoverIn?: (date: Date) => void;
  onDateHoverOut?: () => void;
  onDatePress: (date: Date) => void;
  onMonthChange: (month: Date) => void;
  selectedDate?: Date | null;
  selectedLoanId?: Loan['id'] | null;
  showHeader?: boolean;
}

function getLoanMarkerColor(loan: Loan, theme: Theme) {
  const eta = getLoanETA(loan);

  if (eta === LoanETA.late) {
    return theme.colors['surface.error.default'];
  }

  if (eta === LoanETA.returned) {
    return theme.colors['surface.warning.default'];
  }

  if (eta === LoanETA.scheduled) {
    return theme.colors['surface.info.default'];
  }

  return theme.colors['surface.success.default'];
}

function getLoanMarkerSubtleColor(loan: Loan, theme: Theme) {
  const eta = getLoanETA(loan);

  if (eta === LoanETA.late) {
    return theme.colors['surface.error.subtle'];
  }

  if (eta === LoanETA.returned) {
    return theme.colors['surface.warning.subtle'];
  }

  if (eta === LoanETA.scheduled) {
    return theme.colors['surface.info.subtle'];
  }

  return theme.colors['surface.success.subtle'];
}

function LoanCalendarHeader({
  currentMonth,
  isDesktop = false,
  onMonthChange,
  showHeader = true,
  today,
}: Pick<LoanCalendarProps, 'currentMonth' | 'isDesktop' | 'onMonthChange' | 'showHeader'> & { today: Date }) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');
  const iconColor = theme.colors['text.primary'];
  const changeMonth = (month: Date) => onMonthChange(startOfMonth(month));

  return showHeader ? (
    <Row alignItems="center" gap="spacing.general.x2" justifyContent="space-between">
      <Pressable
        accessibilityLabel={t('calendar.previousMonth')}
        accessibilityRole="button"
        hitSlop={8}
        onPress={() => changeMonth(subMonths(currentMonth, 1))}
        style={({ pressed }) => ({
          alignItems: 'center',
          backgroundColor: pressed ? theme.colors['state-layer.press'] : theme.colors['surface.primary-neutral'],
          borderRadius: theme.borderRadii['radius.full'],
          height: 40,
          justifyContent: 'center',
          width: 40,
        })}
        testID="loan-calendar-previous-month"
      >
        <IconChevronLeft color={iconColor} size={22} strokeWidth={ICON_STROKE_WIDTH} />
      </Pressable>

      <Column alignItems="center" flex={1}>
        <Text fontWeight="600" textAlign="center" variant={isDesktop ? 'headerXSmall' : 'bodyLarge'}>
          {localeAwareFormat(currentMonth, 'MMMM y')}
        </Text>
        <Pressable
          accessibilityRole="button"
          onPress={() => changeMonth(today)}
          style={({ pressed }) => ({ opacity: pressed ? 0.55 : 1, paddingHorizontal: 8, paddingVertical: 2 })}
        >
          <Text color="text.brand" fontSize={13} fontWeight="600">
            {t('calendar.today')}
          </Text>
        </Pressable>
      </Column>

      <Pressable
        accessibilityLabel={t('calendar.nextMonth')}
        accessibilityRole="button"
        hitSlop={8}
        onPress={() => changeMonth(addMonths(currentMonth, 1))}
        style={({ pressed }) => ({
          alignItems: 'center',
          backgroundColor: pressed ? theme.colors['state-layer.press'] : theme.colors['surface.primary-neutral'],
          borderRadius: theme.borderRadii['radius.full'],
          height: 40,
          justifyContent: 'center',
          width: 40,
        })}
        testID="loan-calendar-next-month"
      >
        <IconChevronRight color={iconColor} size={22} strokeWidth={ICON_STROKE_WIDTH} />
      </Pressable>
    </Row>
  ) : null;
}

function LoanCalendarWeekdays({ weekdayDates }: { weekdayDates: Date[] }) {
  return (
    <Row paddingHorizontal="spacing.general.x1">
      {weekdayDates.map((date) => (
        <Text
          color="text.secondary"
          fontSize={11}
          fontWeight="600"
          key={date.toISOString()}
          style={{ flex: 1 }}
          textAlign="center"
        >
          {localeAwareFormat(date, 'EEEEE')}
        </Text>
      ))}
    </Row>
  );
}

function LoanCalendarGrid({
  activeLoanId,
  currentMonth,
  isDesktop = false,
  loansByDay,
  onDateHoverIn,
  onDateHoverOut,
  onDatePress,
  selectedDate,
  today,
  visibleWeeks,
}: Pick<
  LoanCalendarProps,
  'currentMonth' | 'isDesktop' | 'onDateHoverIn' | 'onDateHoverOut' | 'onDatePress' | 'selectedDate'
> & {
  activeLoanId?: Loan['id'] | null;
  loansByDay: Map<number, Loan[]>;
  today: Date;
  visibleWeeks: Date[][];
}) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');

  return (
    <Animated.View layout={LinearTransition.duration(300)}>
      <Column
        borderColor="surface.disabled"
        borderRadius="radius.lg"
        borderWidth={isDesktop ? 1 : 0}
        style={isDesktop ? { overflow: 'hidden' } : undefined}
      >
        {visibleWeeks.map((week, weekIndex) => (
          <Row key={week[0]?.toISOString()}>
            {week.map((date, dayIndex) => {
              const dayLoans = loansByDay.get(date.getTime()) ?? [];
              const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
              const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
              const isToday = isSameDay(date, today);
              const overflowCount = Math.max(0, dayLoans.length - MAX_VISIBLE_LOAN_MARKERS);
              const visibleDayLoans = [
                ...dayLoans.filter((loan) => loan.id === activeLoanId),
                ...dayLoans.filter((loan) => loan.id !== activeLoanId),
              ].slice(0, MAX_VISIBLE_LOAN_MARKERS);
              const dateLabel = t('calendar.dateAccessibility', {
                count: dayLoans.length,
                date: localeAwareFormat(date, 'PPPP'),
              });

              return (
                <Pressable
                  accessibilityLabel={dateLabel}
                  accessibilityRole="button"
                  accessibilityState={{ selected: isSelected }}
                  key={date.toISOString()}
                  onHoverIn={() => onDateHoverIn?.(date)}
                  onHoverOut={onDateHoverOut}
                  onPress={() => onDatePress(date)}
                  style={({ pressed }) => ({
                    alignItems: isDesktop ? 'flex-start' : 'center',
                    backgroundColor: isSelected
                      ? theme.colors['surface.selected']
                      : pressed
                        ? theme.colors['state-layer.hover']
                        : isCurrentMonth
                          ? theme.colors['surface.default']
                          : theme.colors['surface.primary-neutral'],
                    borderBottomColor: theme.colors['surface.disabled'],
                    borderBottomWidth: isDesktop && weekIndex < visibleWeeks.length - 1 ? 1 : 0,
                    borderRightColor: theme.colors['surface.disabled'],
                    borderRightWidth: isDesktop && dayIndex < 6 ? 1 : 0,
                    flex: 1,
                    height: isDesktop ? 84 : 52,
                    justifyContent: isDesktop ? 'flex-start' : 'center',
                    minWidth: 0,
                    opacity: isCurrentMonth ? 1 : 0.55,
                    paddingHorizontal: isDesktop ? 0 : 2,
                    paddingVertical: isDesktop ? 8 : 4,
                  })}
                  testID={`loan-calendar-day-${localeAwareFormat(date, 'yyyy-MM-dd')}`}
                >
                  <View
                    style={{
                      alignItems: 'center',
                      alignSelf: isDesktop ? 'flex-start' : 'center',
                      backgroundColor: isSelected
                        ? theme.colors['surface.brand.strong-alt']
                        : isToday
                          ? theme.colors['surface.brand.subtle']
                          : 'transparent',
                      borderRadius: theme.borderRadii['radius.full'],
                      height: isDesktop ? 26 : 22,
                      justifyContent: 'center',
                      marginLeft: isDesktop ? 8 : 0,
                      width: isDesktop ? 26 : 22,
                    }}
                  >
                    <Text
                      fontSize={isDesktop ? 13 : 12}
                      fontWeight={isSelected || isToday ? '700' : '500'}
                      style={{
                        color: isSelected
                          ? theme.colors['text.inverse.primary']
                          : isToday
                            ? theme.colors['text.brand']
                            : isCurrentMonth
                              ? theme.colors['text.primary']
                              : theme.colors['text.placeholder'],
                      }}
                    >
                      {date.getDate()}
                    </Text>
                  </View>

                  {isDesktop ? (
                    <Column gap="spacing.general.x0,5" minHeight={26} style={{ marginTop: 7, width: '100%' }}>
                      {visibleDayLoans.map((loan) => {
                        const continuesBefore = loanOccursOnDate(loan, addDays(date, -1), today) && date.getDay() !== 0;
                        const continuesAfter = loanOccursOnDate(loan, addDays(date, 1), today) && date.getDay() !== 6;
                        const isActive = activeLoanId === loan.id;
                        const markerColor = getLoanMarkerColor(loan, theme);

                        return (
                          <View
                            key={loan.id}
                            style={{
                              backgroundColor: isActive ? markerColor : getLoanMarkerSubtleColor(loan, theme),
                              borderBottomLeftRadius: continuesBefore ? 0 : 4,
                              borderBottomRightRadius: continuesAfter ? 0 : 4,
                              borderTopLeftRadius: continuesBefore ? 0 : 4,
                              borderTopRightRadius: continuesAfter ? 0 : 4,
                              height: isActive ? 8 : 7,
                              opacity: activeLoanId && !isActive ? 0.42 : 1,
                              overflow: 'hidden',
                              width: '100%',
                            }}
                          >
                            {!isActive ? (
                              <View
                                style={{
                                  backgroundColor: markerColor,
                                  bottom: 0,
                                  height: 2,
                                  left: 0,
                                  opacity: 0.75,
                                  position: 'absolute',
                                  right: 0,
                                }}
                              />
                            ) : null}
                          </View>
                        );
                      })}
                      {overflowCount ? (
                        <Text
                          fontSize={9}
                          fontWeight="700"
                          style={{
                            color: theme.colors['text.secondary'],
                            marginLeft: 6,
                          }}
                        >
                          +{overflowCount}
                        </Text>
                      ) : null}
                    </Column>
                  ) : (
                    <Row
                      alignItems="center"
                      gap="spacing.general.x0,5"
                      justifyContent="center"
                      minHeight={10}
                      style={{ marginTop: 3 }}
                    >
                      {visibleDayLoans.map((loan) => (
                        <View
                          key={loan.id}
                          style={{
                            backgroundColor: getLoanMarkerColor(loan, theme),
                            borderRadius: 3,
                            height: 5,
                            width: 7,
                          }}
                        />
                      ))}
                      {overflowCount ? (
                        <Text
                          fontSize={9}
                          fontWeight="700"
                          style={{
                            color: theme.colors['text.secondary'],
                          }}
                        >
                          +{overflowCount}
                        </Text>
                      ) : null}
                    </Row>
                  )}
                </Pressable>
              );
            })}
          </Row>
        ))}
      </Column>
    </Animated.View>
  );
}

function LoanCalendarLegend() {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');

  return (
    <Row flexWrap="wrap" gap="spacing.general.x4" paddingHorizontal="spacing.general.x1">
      {[
        { color: theme.colors['surface.info.default'], label: t('calendar.legend.scheduled') },
        {
          color: theme.colors['surface.success.default'],
          label: t('calendar.legend.ongoing'),
        },
        {
          color: theme.colors['surface.warning.default'],
          label: t('calendar.legend.returned'),
        },
        { color: theme.colors['surface.error.default'], label: t('calendar.legend.late') },
      ].map(({ color, label }) => (
        <Row alignItems="center" gap="spacing.general.x1" key={label}>
          <View style={{ backgroundColor: color, borderRadius: 3, height: 6, width: 6 }} />
          <Text color="text.secondary" fontSize={11}>
            {label}
          </Text>
        </Row>
      ))}
    </Row>
  );
}

export function LoanCalendar({
  currentMonth,
  highlightedLoanId,
  isDesktop = false,
  loans,
  onDateHoverIn,
  onDateHoverOut,
  onDatePress,
  onMonthChange,
  selectedDate,
  selectedLoanId,
  showHeader = true,
}: LoanCalendarProps) {
  const theme = useTheme<Theme>();
  const [today] = useReducer(
    (current: Date) => current,
    null,
    () => startOfDay(new Date()),
  );
  const days = getCalendarGridDays(currentMonth);
  const activeLoanId = highlightedLoanId ?? selectedLoanId;
  const weekdayDates = days.slice(0, 7);
  const weeks = Array.from({ length: 6 }, (_, weekIndex) => days.slice(weekIndex * 7, weekIndex * 7 + 7));
  const visibleWeeks = isDesktop
    ? weeks.filter((week) => week.some((date) => date.getMonth() === currentMonth.getMonth()))
    : weeks;
  const loansByDay = new Map(
    days.map((date) => [date.getTime(), loans.filter((loan) => loanOccursOnDate(loan, date, today))]),
  );

  return (
    <Column
      backgroundColor="surface.default"
      borderColor="surface.disabled"
      borderRadius="radius.xl"
      borderWidth={1}
      gap="spacing.general.x3"
      padding="spacing.general.x4"
      style={{
        boxShadow: `0px 4px 16px ${theme.colors['shadow.strong']}`,
      }}
      testID="loan-calendar"
    >
      <LoanCalendarHeader
        currentMonth={currentMonth}
        isDesktop={isDesktop}
        onMonthChange={onMonthChange}
        showHeader={showHeader}
        today={today}
      />
      <LoanCalendarWeekdays weekdayDates={weekdayDates} />
      <LoanCalendarGrid
        activeLoanId={activeLoanId}
        currentMonth={currentMonth}
        isDesktop={isDesktop}
        loansByDay={loansByDay}
        onDateHoverIn={onDateHoverIn}
        onDateHoverOut={onDateHoverOut}
        onDatePress={onDatePress}
        selectedDate={selectedDate}
        today={today}
        visibleWeeks={visibleWeeks}
      />
      <LoanCalendarLegend />
    </Column>
  );
}
